import type { Contact, ContactType } from "../types";
import { CONTACT_TYPES } from "../types";

/**
 * Almikia Epic EC2a: the contact types (one address book) and the rules of which types ask for
 * extra fields. Pure data, no UI: TASK-120 builds the form and the filters on top of it.
 */

/**
 * Spanish i18n label key of every contact type. The messages live in the Spanish messages file
 * (TASK-120); the key is `resources.contacts.contact_types.<type>`.
 */
export const CONTACT_TYPE_LABEL_KEYS: Record<ContactType, string> = {
  lead_buyer: "resources.contacts.contact_types.lead_buyer",
  lead_tenant: "resources.contacts.contact_types.lead_tenant",
  client: "resources.contacts.contact_types.client",
  tenant: "resources.contacts.contact_types.tenant",
  guarantor: "resources.contacts.contact_types.guarantor",
  owner_seller: "resources.contacts.contact_types.owner_seller",
  owner_lessor: "resources.contacts.contact_types.owner_lessor",
  colega: "resources.contacts.contact_types.colega",
  supplier: "resources.contacts.contact_types.supplier",
  ally: "resources.contacts.contact_types.ally",
  developer: "resources.contacts.contact_types.developer",
};

/**
 * Types a person never picks by hand: later batches set them from the SQL function
 * `private.add_contact_type` (a buyer lead that closes a purchase becomes a client, a tenant
 * lead that signs becomes a tenant). The form shows them read-only.
 */
export const AUTO_SET_CONTACT_TYPES: readonly ContactType[] = [
  "client",
  "tenant",
];

/** Types a person can choose when creating or editing a contact. */
export const MANUAL_CONTACT_TYPES: readonly ContactType[] =
  CONTACT_TYPES.filter((type) => !AUTO_SET_CONTACT_TYPES.includes(type));

/** The extra field groups a contact type asks for (one group per dependent column set). */
export type ContactTypeExtra = "colega" | "supplier" | "guarantor";

/** Which contact type asks for which extra field group (mirrors the DB guard). */
export const CONTACT_TYPE_EXTRAS: Readonly<
  Record<ContactTypeExtra, ContactType>
> = {
  colega: "colega",
  supplier: "supplier",
  guarantor: "guarantor",
};

/** Whether the selected types ask for the given extra field group. */
export const typesRequireExtra = (
  types: readonly ContactType[] | null | undefined,
  extra: ContactTypeExtra,
): boolean => (types ?? []).includes(CONTACT_TYPE_EXTRAS[extra]);

/**
 * EXTENSION POINT (part 4 / EC2d): the types whose Show page lists the owner's properties. The
 * `ContactOwnerPropertiesSlot` below is mounted by ContactShow for these types.
 */
export const OWNER_PROPERTY_LINK_TYPES: readonly ContactType[] = [
  "owner_seller",
  "owner_lessor",
];

/**
 * EXTENSION POINT (developers): the types linked to a developer (`contacts.developer_id`,
 * nothing writes it yet). The `ContactDeveloperSlot` below is mounted by ContactShow.
 */
export const DEVELOPER_LINK_TYPES: readonly ContactType[] = ["developer"];

/** Whether the contact has at least one of the given types. */
export const hasAnyContactType = (
  contact: Pick<Contact, "contact_types">,
  types: readonly ContactType[],
): boolean => (contact.contact_types ?? []).some((t) => types.includes(t));

type ContactSlotProps = { contact?: Contact };

/**
 * EXTENSION POINT (EC2d): the owner's properties block of ContactShow. Renders nothing until the
 * property-owner link of EC2d exists; ContactShow can mount it now without a redesign.
 */
export const ContactOwnerPropertiesSlot = (_props: ContactSlotProps) => null;

/**
 * EXTENSION POINT (developers): the developer link block of ContactShow. Renders nothing until
 * the developer extension exists; ContactShow can mount it now without a redesign.
 */
export const ContactDeveloperSlot = (_props: ContactSlotProps) => null;
